import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Radio, Space, Alert, Spin } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { createUser, checkUserExists } from "./api";

type PasswordOption = "set_now" | "send_invite";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  onSuccess: () => void;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onClose,
  restaurantId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [passwordOption, setPasswordOption] = useState<PasswordOption>("send_invite");

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setUserExists(false);
      setPasswordOption("send_invite");
    }
  }, [open, form]);

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    if (!email) {
      setUserExists(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return;
    }

    setCheckingEmail(true);
    checkUserExists(
      restaurantId,
      email,
      (data) => {
        setUserExists(data.exists);
        setCheckingEmail(false);
      },
      () => {
        setCheckingEmail(false);
      }
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const requestData: {
        email: string;
        roles: string[];
        password?: string;
      } = {
        email: values.email.trim(),
        roles: values.role ? [values.role] : [],
      };

      // Only include password if user chose to set it now
      if (!userExists && passwordOption === "set_now" && values.password) {
        requestData.password = values.password;
      }

      createUser(
        restaurantId,
        requestData,
        (response) => {
          setLoading(false);
          let message: string;
          if (response.is_new_user) {
            message =
              passwordOption === "send_invite"
                ? t("user_created_send_invite_hint")
                : t("user_created_successfully");
          } else {
            message = t("user_added_to_restaurant");
          }
          showSuccess(message);
          onSuccess();
        },
        (err) => {
          setLoading(false);
          notifyError(t("failed_to_create_user"), err);
        }
      );
    } catch {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={
        <Space>
          <UserAddOutlined />
          {t("add_user")}
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("add")}
      cancelText={t("cancel")}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="email"
          label={t("email")}
          rules={[
            { required: true, message: t("please_enter_email") },
            { type: "email", message: t("please_enter_valid_email") },
          ]}
        >
          <Input
            placeholder={t("enter_email")}
            onBlur={handleEmailBlur}
            suffix={checkingEmail ? <Spin size="small" /> : null}
          />
        </Form.Item>

        {userExists && (
          <Alert
            type="info"
            message={t("user_exists_info")}
            description={t("user_exists_description")}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item name="role" label={t("role")}>
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="">{t("role_regular_user")}</Radio>
              <Radio value="Admin">{t("role_admin")}</Radio>
              <Radio value="Manager">{t("role_manager")}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {!userExists && (
          <>
            <Form.Item label={t("password_option")}>
              <Radio.Group
                value={passwordOption}
                onChange={(e) => setPasswordOption(e.target.value)}
              >
                <Space direction="vertical">
                  <Radio value="send_invite">{t("send_invite_email")}</Radio>
                  <Radio value="set_now">{t("set_password_now")}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {passwordOption === "set_now" && (
              <Form.Item
                name="password"
                label={t("password")}
                rules={[
                  { required: true, message: t("please_enter_password") },
                  { min: 6, message: t("password_min_length") },
                ]}
              >
                <Input.Password placeholder={t("enter_password")} />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};
